import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import { Inject, Injectable, PLATFORM_ID } from "@angular/core";

import {
  CookieService,
  CookieServiceSetOptions,
} from "../interfaces/cookie-service.interface";

@Injectable()
export class BaseCookieService implements CookieService {
  private readonly documentIsAccessible: boolean;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    /* tslint:disable-next-line:ban-types */
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.documentIsAccessible = isPlatformBrowser(this.platformId);
  }

  check(name: string): boolean {
    if (!this.documentIsAccessible) {
      return false;
    }
    name = encodeURIComponent(name);
    const regExp: RegExp = this.getCookieRegExp(name);

    return regExp.test(this.document.cookie);
  }

  get(name: string): string {
    if (this.documentIsAccessible && this.check(name)) {
      name = encodeURIComponent(name);

      const regExp: RegExp = this.getCookieRegExp(name);
      const result: RegExpExecArray = regExp.exec(this.document.cookie);

      return decodeURIComponent(result[1]);
    } else {
      return null;
    }
  }

  getAll(): {} {
    if (!this.documentIsAccessible) {
      return {};
    }

    const cookies: {} = {};
    const document: any = this.document;

    if (document.cookie && document.cookie !== "") {
      const split: Array<string> = document.cookie.split(";");
      /* tslint:disable-next-line:prefer-for-of */
      for (let i = 0; i < split.length; i += 1) {
        const currentCookie: Array<string> = split[i].split("=");

        currentCookie[0] = currentCookie[0].replace(/^ /, "");
        //@ts-ignore

        cookies[decodeURIComponent(currentCookie[0])] = decodeURIComponent(
          currentCookie[1]
        );
      }
    }

    return cookies;
  }

  put(
    name: string,
    value: string,
    options: Partial<CookieServiceSetOptions> = {
      sameSite: "None",
      secure: true,
    }
  ): void {
    if (!this.documentIsAccessible) {
      return;
    }

    let cookieString: string =
      encodeURIComponent(name) + "=" + encodeURIComponent(value) + ";";

    if (options.expires) {
      if (typeof options.expires === "number") {
        const dateExpires: Date = new Date(
          new Date().getTime() + options.expires * 1000 * 60 * 60 * 24
        );

        cookieString += "expires=" + dateExpires.toUTCString() + ";";
      } else {
        cookieString += "expires=" + options.expires.toUTCString() + ";";
      }
    }

    if (options.path) {
      cookieString += "path=" + options.path + ";";
    }

    if (options.domain) {
      cookieString += "domain=" + options.domain + ";";
    }

    if (options.secure) {
      cookieString += "secure;";
    }

    if (options.sameSite) {
      cookieString += "sameSite=" + options.sameSite + ";";
    }

    this.document.cookie = cookieString;
  }

  remove(name: string, path?: string, domain?: string): void {
    if (!this.documentIsAccessible) {
      return;
    }

    this.put(name, "", {
      path,
      domain,
      expires: new Date("Thu, 01 Jan 1970 00:00:01 GMT"),
    });
  }

  removeAll(path?: string, domain?: string): void {
    if (!this.documentIsAccessible) {
      return;
    }

    const cookies: any = this.getAll();

    for (const cookieName in cookies) {
      if (cookies.hasOwnProperty(cookieName)) {
        this.remove(cookieName, path, domain);
      }
    }
  }

  /**
   * @param name Cookie name
   */
  protected getCookieRegExp(name: string): RegExp {
    const escapedName: string = name.replace(
      /([\[\]\{\}\(\)\|\=\;\+\?\,\.\*\^\$])/gi,
      "\\$1"
    );

    return new RegExp(
      "(?:^" + escapedName + "|;\\s*" + escapedName + ")=(.*?)(?:;|$)",
      "g"
    );
  }
}
